import type {
  Connection,
  ConnEnd,
  ProductInstance,
  ProductTemplate,
  Vec2,
} from '../shared/types';
import { placeCopy } from './catalogueCopy';

export interface MultiPartPlacement {
  groupId: string;
  instances: ProductInstance[];
  connections: Connection[];
}

/**
 * Instantiate a multi-part product at `world`: each part becomes its own
 * instance (so it can be moved independently), all sharing a group id, and the
 * template's connections are mapped onto the new instance ids. `gen` returns a
 * fresh unique id on each call.
 */
export function buildMultiPartPlacement(
  t: ProductTemplate,
  world: Vec2,
  gen: () => string
): MultiPartPlacement {
  const groupId = gen();
  const idMap: Record<string, string> = {};
  const instances: ProductInstance[] = [];

  (t.parts || []).forEach((part, i) => {
    const id = gen();
    idMap[part.id] = id;
    instances.push({
      ...placeCopy(t, gen, { part, first: i === 0 }),
      id,
      groupId,
      partId: part.id,
      x: world.x + part.offset.x,
      y: world.y + part.offset.y,
      rotation: part.rotation || 0,
    });
  });

  const connections: Connection[] = [];
  for (const c of t.connections || []) {
    if (idMap[c.from] && idMap[c.to]) connections.push(partConnection(c, gen(), idMap[c.from], idMap[c.to]));
  }
  return { groupId, instances, connections };
}

/** A product's connection between two of its parts, laid between their placed
 *  copies `from` / `to`: its discipline and text, and the ends it is glued by
 *  (a port, its height) and the pipe it is made of, so it follows the parts as
 *  they move. */
export function partConnection(c: Connection, id: string, from: string, to: string): Connection {
  const out: Connection = { id, from, to, lines: c.lines, label: c.label, type: c.type };
  if (c.labelI18n) out.labelI18n = { ...c.labelI18n };
  // A port is found on whichever part the end is on; a `ref` would name the
  // copy it was taken from.
  const end = (e: ConnEnd): ConnEnd => { const o = structuredClone(e); if (o.port) delete o.ref; return o; };
  if (c.fromEnd) out.fromEnd = end(c.fromEnd);
  if (c.toEnd) out.toEnd = end(c.toEnd);
  if (c.pipe) out.pipe = { ...c.pipe };
  if (c.route) out.route = c.route;
  return out;
}

/** A placed group's connections between its parts laid again from its
 *  product's (`partConnection`; `idOf`: a product part's id → the copy's part
 *  id). Each takes the place of one of `old` that joined the same two parts,
 *  either way round, in turn: its id — so what names it (the schematic's wire,
 *  its edits) still does — its route and look, and what was set on it in the
 *  drawing (who lays it, a typed length, its run height, its tag's place). One
 *  the group had none for gets a fresh id (`gen`). */
export function relaidPartConnections(t: Pick<ProductTemplate, 'connections'>, idOf: ReadonlyMap<string, string>, old: readonly Connection[], gen: () => string): Connection[] {
  const left = [...old];
  const out: Connection[] = [];
  for (const tc of t.connections ?? []) {
    const from = idOf.get(tc.from), to = idOf.get(tc.to);
    if (!from || !to) continue;
    const i = left.findIndex((c) => (c.from === from && c.to === to) || (c.from === to && c.to === from));
    const prev = i >= 0 ? left.splice(i, 1)[0] : undefined;
    const c = partConnection(tc, prev?.id ?? gen(), from, to);
    if (prev) {
      for (const k of ['waypoints', 'thickness', 'style', 'installedBy', 'length', 'runZ', 'labelT', 'layer'] as const) {
        if (prev[k] !== undefined) (c as unknown as Record<string, unknown>)[k] = prev[k];
      }
    }
    out.push(c);
  }
  return out;
}
